import { useEffect, useRef, useState } from 'react';
import {
  GoogleAuthProvider,
  isSignInWithEmailLink,
  sendSignInLinkToEmail,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signInWithPopup,
  signOut,
  type User,
} from 'firebase/auth';
import { auth } from '../firebase';

// The universal signup/login surface (docs/onboarding.md §2–§3) — deliberately OUTSIDE
// AuthProvider: the console's establish() auto-exchanges /api/session, which rejects unknown
// identities AND signs the Firebase user out on failure — both wrong mid-signup. This page
// owns its Firebase calls, its own email-link continue URL (AuthProvider's hardcodes the
// origin root and strips the query — it would discard app/return_to/state, §3/§8), and signs
// the client session out after issuing the redirect (§7: the surface keeps no session).

const SIGNUP_HOST = 'signup.captomi.com';
const CONSOLE_URL = 'https://console.captomi.com';
const EMAIL_FOR_SIGN_IN = 'signupEmailForSignIn';
// Decision 5 (§11): bump when the published product terms version changes; recorded on the
// signup auth-event. Matches the version line on captomi.com/product-terms.
const TOS_VERSION = '2026-06-11';
const TERMS_URL = 'https://captomi.com/product-terms';
const PRIVACY_URL = 'https://captomi.com/privacy';

const STATE_PATTERN = /^[A-Za-z0-9._~-]{1,512}$/;

// reCAPTCHA v3 (same mechanism as the legacy linkedin form). The site key is public and
// build-time; loads lazily, and the whole thing no-ops when unset (dev) so the flow still works.
const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY as string | undefined;

declare global {
  interface Window {
    grecaptcha?: { ready: (cb: () => void) => void; execute: (key: string, opts: { action: string }) => Promise<string> };
  }
}

let recaptcha_loading: Promise<void> | null = null;
function load_recaptcha(): Promise<void> {
  if (!RECAPTCHA_SITE_KEY) return Promise.resolve();
  if (window.grecaptcha) return Promise.resolve();
  if (!recaptcha_loading) {
    recaptcha_loading = new Promise<void>((resolve, reject) => {
      const s = document.createElement('script');
      s.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('recaptcha load failed'));
      document.head.appendChild(s);
    });
  }
  return recaptcha_loading;
}

// Returns a fresh action-scoped token, or undefined when no key is configured (the BFF skips
// verification then). Never throws — a reCAPTCHA hiccup must not block a legitimate signup
// harder than the server's own decision.
async function recaptcha_token(action: string): Promise<string | undefined> {
  if (!RECAPTCHA_SITE_KEY) return undefined;
  try {
    await load_recaptcha();
    const gre = window.grecaptcha;
    if (!gre) return undefined;
    return await new Promise<string>((resolve, reject) => {
      gre.ready(() => gre.execute(RECAPTCHA_SITE_KEY!, { action }).then(resolve, reject));
    });
  } catch {
    return undefined;
  }
}

export interface SignupParams {
  app: string;
  state: string;
  return_to?: string;
}

// The §2 entry triple, shape-checked (the BFF re-validates everything authoritatively).
export function parse_signup_params(search: string): SignupParams | null {
  const q = new URLSearchParams(search);
  const app = q.get('app') ?? '';
  const state = q.get('state') ?? '';
  if (!/^[a-z0-9_]{1,64}$/.test(app)) return null;
  if (!STATE_PATTERN.test(state)) return null;
  const return_to = q.get('return_to') ?? undefined;
  return { app, state, ...(return_to ? { return_to } : {}) };
}

// The email-link continue URL must carry the whole triple through the round trip (§3).
export function build_continue_url(origin: string, params: SignupParams): string {
  const url = new URL('/signup', origin);
  url.searchParams.set('app', params.app);
  if (params.return_to) url.searchParams.set('return_to', params.return_to);
  url.searchParams.set('state', params.state);
  return url.toString();
}

interface AppInfo {
  app_code: string;
  display_name: string;
  self_serve: boolean;
  logo_url?: string;
  blurb?: string;
}

type Phase =
  | { step: 'loading' }
  | { step: 'error'; message: string }
  | { step: 'methods'; notice?: string }
  | { step: 'link_sent'; email: string }
  | { step: 'interstitial'; user: User } // explicit-gesture gate (§3)
  | { step: 'company'; user: User }
  | { step: 'pending'; tenant_name: string }
  | { step: 'redirecting' };

const GENERIC_REJECTION =
  "We couldn't sign you in with that account. If your business already uses Captomi, ask a workspace owner to invite you.";

async function post_json(path: string, body: unknown): Promise<{ status: number; json: any }> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  return { status: res.status, json: await res.json().catch(() => ({})) };
}

export function SignupPage() {
  const [params] = useState(() => parse_signup_params(window.location.search));
  const [info, setInfo] = useState<AppInfo | null>(null);
  const [phase, setPhase] = useState<Phase>({ step: 'loading' });
  const fresh_signin = useRef(false); // a sign-in completed in THIS visit is itself the gesture

  // Invalid entry: on the vanity host bounce to the console (G0); on the console host a
  // static error (§2 — unknown/missing app never redirects anywhere else).
  useEffect(() => {
    if (params) return;
    if (window.location.hostname === SIGNUP_HOST) {
      window.location.replace(CONSOLE_URL);
      return;
    }
    setPhase({ step: 'error', message: 'This sign-up link is missing or invalid. Check the link from your app.' });
  }, [params]);

  // Load the app branding; a 404 covers unknown / deactivated / un-extended apps alike.
  useEffect(() => {
    if (!params) return;
    fetch(`/api/signup/app?app=${encodeURIComponent(params.app)}`)
      .then(async (res) => {
        if (!res.ok) throw new Error('not_found');
        setInfo(await res.json());
      })
      .catch(() =>
        setPhase({ step: 'error', message: 'This sign-up link is invalid. Check the link from your app.' }),
      );
  }, [params]);

  // Warm the reCAPTCHA script as soon as we have an app to sign up for, so a token is ready
  // the instant the user submits the company step. No-ops when no site key is configured.
  useEffect(() => {
    if (info) void load_recaptcha();
  }, [info]);

  // Complete an email magic-link landing — preserving the query triple (the link's continue
  // URL carried it; we strip only Firebase's own params afterwards by re-writing the URL).
  useEffect(() => {
    if (!params || !info) return;
    if (!isSignInWithEmailLink(auth, window.location.href)) {
      // Persisted client session (Firebase local persistence) → interstitial, never a
      // silent hand-off (§3 explicit-gesture gate).
      if (auth.currentUser) setPhase({ step: 'interstitial', user: auth.currentUser });
      else setPhase({ step: 'methods' });
      return;
    }
    let email = window.localStorage.getItem(EMAIL_FOR_SIGN_IN);
    if (!email) email = window.prompt('Confirm the email this sign-in link was sent to') ?? '';
    if (!email) {
      setPhase({ step: 'methods', notice: GENERIC_REJECTION });
      return;
    }
    signInWithEmailLink(auth, email, window.location.href)
      .then((cred) => {
        window.localStorage.removeItem(EMAIL_FOR_SIGN_IN);
        window.history.replaceState({}, '', build_continue_url(window.location.origin, params));
        fresh_signin.current = true;
        void resolve_with(cred.user);
      })
      .catch(() => setPhase({ step: 'methods', notice: 'That sign-in link is invalid or expired. Try again.' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, info]);

  async function resolve_with(user: User): Promise<void> {
    if (!params) return;
    const id_token = await user.getIdToken();
    const { status, json } = await post_json('/api/signup/resolve', {
      id_token,
      app: params.app,
      ...(params.return_to ? { return_to: params.return_to } : {}),
      state: params.state,
    });
    await handle_outcome(status, json, user);
  }

  async function handle_outcome(status: number, json: any, user: User): Promise<void> {
    if (status === 200 && json.redirect) {
      setPhase({ step: 'redirecting' });
      // §7: the surface keeps no session — client-side included.
      await signOut(auth).catch(() => {});
      window.location.assign(json.redirect);
      return;
    }
    if (status === 200 && json.next === 'company') {
      setPhase({ step: 'company', user });
      return;
    }
    if (status === 200 && json.pending) {
      await signOut(auth).catch(() => {});
      setPhase({ step: 'pending', tenant_name: json.tenant_name ?? '' });
      return;
    }
    await signOut(auth).catch(() => {});
    setPhase({ step: 'methods', notice: GENERIC_REJECTION });
  }

  async function sign_in_google(): Promise<void> {
    try {
      const cred = await signInWithPopup(auth, new GoogleAuthProvider());
      fresh_signin.current = true;
      await resolve_with(cred.user);
    } catch {
      setPhase({ step: 'methods', notice: 'Google sign-in was cancelled or failed. Try again.' });
    }
  }

  async function send_link(email: string): Promise<void> {
    if (!params) return;
    window.localStorage.setItem(EMAIL_FOR_SIGN_IN, email);
    await sendSignInLinkToEmail(auth, email, {
      url: build_continue_url(window.location.origin, params),
      handleCodeInApp: true,
    });
    setPhase({ step: 'link_sent', email });
  }

  async function sign_in_password(email: string, password: string): Promise<void> {
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      fresh_signin.current = true;
      await resolve_with(cred.user);
    } catch {
      setPhase({ step: 'methods', notice: GENERIC_REJECTION });
    }
  }

  async function complete_signup(input: {
    company: string;
    first_name: string;
    last_name: string;
  }): Promise<void> {
    if (!params || phase.step !== 'company') return;
    const [id_token, captcha] = await Promise.all([
      phase.user.getIdToken(),
      recaptcha_token('signup'),
    ]);
    const { status, json } = await post_json('/api/signup/complete', {
      id_token,
      app: params.app,
      ...(params.return_to ? { return_to: params.return_to } : {}),
      state: params.state,
      ...input,
      tos_version: TOS_VERSION,
      ...(captcha ? { recaptcha_token: captcha } : {}),
    });
    await handle_outcome(status, json, phase.user);
  }

  if (!params || phase.step === 'loading' || (!info && phase.step !== 'error')) {
    return (
      <Centered>
        <p className="muted">Loading…</p>
      </Centered>
    );
  }
  if (phase.step === 'error') {
    return (
      <SignupCard>
        <p className="error" style={{ textAlign: 'center' }}>{phase.message}</p>
      </SignupCard>
    );
  }

  switch (phase.step) {
    case 'redirecting':
      return (
        <SignupCard info={info!}>
          <p className="muted" style={{ textAlign: 'center' }}>Taking you back…</p>
        </SignupCard>
      );
    case 'pending':
      return (
        <SignupCard info={info!}>
          <p style={{ textAlign: 'center' }}>
            You've asked to join <strong>{phase.tenant_name}</strong> — a workspace owner or
            admin has to approve you. Try signing in again once you're approved.
          </p>
        </SignupCard>
      );
    case 'link_sent':
      return (
        <SignupCard info={info!}>
          <p style={{ textAlign: 'center' }}>
            We sent a sign-in link to <strong>{phase.email}</strong>.
          </p>
          <p className="muted" style={{ textAlign: 'center', fontSize: 13 }}>
            Open it in this same browser to continue.
          </p>
        </SignupCard>
      );
    case 'interstitial':
      return (
        <SignupCard info={info!}>
          <p style={{ textAlign: 'center' }}>
            Continue as <strong>{phase.user.email}</strong>?
          </p>
          <button className="btn" style={{ width: '100%' }} onClick={() => void resolve_with(phase.user)}>
            Continue to {info!.display_name}
          </button>
          <button
            className="btn secondary"
            style={{ width: '100%', marginTop: 8 }}
            onClick={async () => {
              await signOut(auth).catch(() => {});
              setPhase({ step: 'methods' });
            }}
          >
            Use a different account
          </button>
        </SignupCard>
      );
    case 'company':
      return (
        <CompanyStep info={info!} email={phase.user.email ?? ''} user={phase.user} onSubmit={complete_signup} />
      );
    case 'methods':
      return (
        <MethodsStep
          info={info!}
          notice={phase.notice}
          onGoogle={sign_in_google}
          onLink={send_link}
          onPassword={sign_in_password}
        />
      );
  }
}

// Visual shell mirroring components/Login.tsx exactly — same centered grid, same 360px
// brand card, same logo header. The signup surface must read as the same product.
function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 16 }}>
      {children}
    </div>
  );
}

function SignupCard({ info, children }: { info?: AppInfo; children: React.ReactNode }) {
  return (
    <Centered>
      <div className="card" style={{ width: 360, maxWidth: '100%' }}>
        <img
          src={info?.logo_url ?? '/img/captomi-logo.png'}
          alt="Captomi"
          style={{ height: 64, width: 'auto', display: 'block', margin: '0 auto 20px' }}
          onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
        />
        <h2 style={{ marginTop: 0, textAlign: 'center' }}>{info?.display_name ?? 'Captomi'}</h2>
        {info?.blurb ? (
          <p className="muted" style={{ textAlign: 'center', marginTop: -8 }}>{info.blurb}</p>
        ) : null}
        {children}
      </div>
    </Centered>
  );
}

const INPUT_STYLE = { width: '100%', padding: 8, marginBottom: 8 } as const;

function MethodsStep({
  info, notice, onGoogle, onLink, onPassword,
}: {
  info: AppInfo;
  notice?: string;
  onGoogle: () => Promise<void>;
  onLink: (email: string) => Promise<void>;
  onPassword: (email: string, password: string) => Promise<void>;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setErr(null);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  }

  return (
    <SignupCard info={info}>
      <p className="muted" style={{ textAlign: 'center', marginTop: -8 }}>
        {info.self_serve ? 'Sign up or sign in to continue' : 'Sign in to continue'}
      </p>
      {notice && <p className="error">{notice}</p>}
      {err && <p className="error">{err}</p>}

      <button className="btn" style={{ width: '100%' }} disabled={busy} onClick={() => void run(onGoogle)}>
        Continue with Google
      </button>

      <div style={{ textAlign: 'center', margin: '14px 0', color: 'var(--muted)' }}>or</div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!email) { setErr('Enter your email first.'); return; }
          void run(() => onLink(email));
        }}
      >
        <input
          type="email"
          placeholder="you@business.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          style={INPUT_STYLE}
        />
        <input
          type="password"
          placeholder="Password (if you have one)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          style={{ ...INPUT_STYLE, marginBottom: 12 }}
        />
        {password ? (
          <button
            className="btn"
            style={{ width: '100%' }}
            disabled={busy || !email}
            type="button"
            onClick={() => void run(() => onPassword(email, password))}
          >
            Sign in
          </button>
        ) : (
          <button className="btn" style={{ width: '100%' }} disabled={busy || !email} type="submit">
            Email me a sign-in link
          </button>
        )}
      </form>
      <p className="muted" style={{ fontSize: 13, textAlign: 'center', marginBottom: 0 }}>
        New here? Use Google or the email link — no password needed.
      </p>
    </SignupCard>
  );
}

// Exported for the R1 copy test (docs/archive/onboarding-phase2.md §7) — not part of the page's API.
export function CompanyStep({
  info, email, user, onSubmit,
}: {
  info: AppInfo;
  email: string;
  user: User;
  onSubmit: (input: { company: string; first_name: string; last_name: string }) => Promise<void>;
}) {
  const display = user.displayName ?? '';
  const [first_name, setFirst] = useState(display.split(' ')[0] ?? '');
  const [last_name, setLast] = useState(display.split(' ').slice(1).join(' '));
  const [company, setCompany] = useState('');
  const [tos_accepted, setTos] = useState(false);
  const [busy, setBusy] = useState(false);
  const can_submit = Boolean(company.trim() && first_name.trim() && last_name.trim() && tos_accepted && !busy);
  return (
    <SignupCard info={info}>
      <p className="muted" style={{ textAlign: 'center', marginTop: -8 }}>
        Set up your workspace — signing up as <strong>{email}</strong>
      </p>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (!can_submit) return;
          setBusy(true);
          try {
            await onSubmit({ company: company.trim(), first_name: first_name.trim(), last_name: last_name.trim() });
          } finally {
            setBusy(false);
          }
        }}
      >
        <input placeholder="First name" value={first_name} onChange={(e) => setFirst(e.target.value)} style={INPUT_STYLE} />
        <input placeholder="Last name" value={last_name} onChange={(e) => setLast(e.target.value)} style={INPUT_STYLE} />
        <input placeholder="Company name" value={company} onChange={(e) => setCompany(e.target.value)} style={{ ...INPUT_STYLE, marginBottom: 4 }} />
        {/* #77: outcome-neutral copy — join-vs-create is decided server-side in the
            transaction; a client pre-check would be a tenant-existence oracle. */}
        <p className="muted" style={{ fontSize: 13, marginTop: 0, marginBottom: 12 }}>
          We'll create your workspace, or add you to your team's if one already exists.
        </p>
        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, marginBottom: 12 }}>
          <input type="checkbox" checked={tos_accepted} onChange={(e) => setTos(e.target.checked)} style={{ marginTop: 2 }} />
          <span>
            I agree to the <a href={TERMS_URL} target="_blank" rel="noreferrer">Product Terms</a> and{' '}
            <a href={PRIVACY_URL} target="_blank" rel="noreferrer">Privacy Policy</a>
          </span>
        </label>
        <button className="btn" style={{ width: '100%' }} disabled={!can_submit} type="submit">
          Continue
        </button>
      </form>
    </SignupCard>
  );
}
